import type { Lang, LorsCopy } from "./translations";

export type June2Headline = { slug: string; headline: string; subhead: string };

// Slugs match the `h=` query-string values from the ad set.
export const june2Headlines: June2Headline[] = [
  {
    slug: "dont-let-a-judge-decide",
    headline: "Don't Let a Judge Decide What Happens to Your Home.",
    subhead:
      "Join us June 2nd in Santa Rosa — learn how to keep your home and assets out of probate court.",
  },
  {
    slug: "your-will-wont-keep-family-out-of-court",
    headline: "Your Will Won't Keep Your Family Out of Court. This Will.",
    subhead:
      "Free workshop — June 2nd in Santa Rosa. Discover what a will can't do, and what actually keeps your family out of probate.",
  },
  {
    slug: "one-step-away-from-probate",
    headline: "Most Sonoma County Families Are One Step Away from Probate. Are You?",
    subhead:
      "Find out at our free June 2nd workshop in Santa Rosa — before it becomes your family's problem.",
  },
  {
    slug: "california-probate-is-public",
    headline: "California Probate Is Public. Your Family's Finances Don't Have to Be.",
    subhead:
      "Free workshop in Santa Rosa, June 2nd — learn how to keep your family's financial affairs out of the public record.",
  },
  {
    slug: "without-a-trust-the-court-gets-involved",
    headline: "Your Home Is Worth $800K+. Without a Trust, the Court Gets Involved.",
    subhead:
      "Free Santa Rosa workshop, June 2nd — find out how to protect your home and pass it to your family without probate.",
  },
  {
    slug: "probate-can-take-18-months",
    headline: "Probate Can Take 18 Months. Your Family Shouldn't Have to Wait.",
    subhead:
      "Join us June 2nd in Santa Rosa — learn how to spare your family the delays, costs, and stress of California probate.",
  },
  {
    slug: "pass-home-to-kids-without-court",
    headline: "How to Pass Your Home to Your Kids Without Going Through Court.",
    subhead:
      "Free workshop — June 2nd in Santa Rosa. A plain-English evening on keeping your home out of probate.",
  },
  {
    slug: "kids-shouldnt-need-a-lawyer-to-inherit",
    headline: "Your Kids Shouldn't Need a Lawyer and a Judge Just to Inherit Your House.",
    subhead:
      "Free event in Santa Rosa, June 2nd — learn how to make things simple for the people you love most.",
  },
  {
    slug: "surprisingly-simple-way-to-skip-probate",
    headline: "The Surprisingly Simple Way to Skip Probate Court Entirely.",
    subhead:
      "Free Santa Rosa workshop, June 2nd — learn how a living trust protects your family and keeps your assets out of court.",
  },
  {
    slug: "weeks-to-set-up-years-to-probate",
    headline:
      "A Living Trust Takes Weeks to Set Up. Probate Takes Years. Which Would You Rather Leave Your Family?",
    subhead:
      "Free workshop — June 2nd in Santa Rosa. The choice is easier than you think.",
  },
];

const baseEn = {
  eyebrow: "Free Estate Planning Workshop",
  headline: june2Headlines[0].headline,
  headlineBreakBefore: "",
  subhead: june2Headlines[0].subhead,
  subheadLabel: "A note from Ruby:",
  learnTitle: "What Most Sonoma County Families Don't Realize Until It's Too Late",
  learnItems: [
    "Why a will alone won't keep your home out of California probate court",
    "How a properly funded living trust passes your home directly to your family — no court, no delays",
    "What every Sonoma County homeowner needs to know about California's probate threshold",
    "California probate fees on a $700,000 estate can exceed $46,000. A solid estate plan? A fraction of that — and your family gets everything, faster.",
  ],
  quote:
    "Ruby Steinbrecher was fantastic. The family trust workshop was clear, informative, and extremely helpful for getting started with estate planning.",
  quoteAttribution: "Shawn H., Workshop Attendee",
  formDate: { short: "Tue, June 2nd", long: "Tuesday, June 2nd" },
  formTime: "4:00–5:00 PM",
  formVenueLine1: "North Coast Builders Exchange",
  formVenueLine2: "1030 Apollo Way, Santa Rosa, CA 95407",
  formTitle: "Reserve Your Seat",
  formSubtitle: "Free to attend. Limited seating available.",
  formFirstName: "First name",
  formLastName: "Last name",
  formEmail: "Email",
  formPhone: "Phone",
  formSmsConsent:
    "I consent to receive text messages from the Law Office of Ruby Steinbrecher about this event, and occasional marketing messages. Message & data rates may apply. Text HELP for assistance, reply STOP to opt out.",
  formEnewsConsent:
    "I'd like to receive occasional email updates from the Law Office of Ruby Steinbrecher.",
  formPrivacy: "Privacy Policy",
  formSubmit: "Reserve My Seat",
  formSubmitting: "Reserving your seat…",
  formTrust: "⭐ Trusted by local families | 81 Five-Star Reviews",
  getDirections: "Get Directions",
  footerCopyright: "© 2026 Ruby Steinbrecher",
  footerPrivacy: "Privacy Policy",
  testimonialsTitle: "What Families Are Saying",
  aboutTitle: "Meet Ruby Steinbrecher",
  aboutRole: "Attorney",
  aboutBody: [
    "I have practiced law since 2007, first working at a medium-sized firm in San Francisco handling civil litigation, and then moving into estate planning, small business and contract law since 2010. I value people, the planet, and prosperity that does not adversely affect the former.",
    "Prior to entering the legal field, I had a variety of business and life experience that has only enhanced my ability to serve clients: Road Manager for Ken Kesey's bus trip to Great Britain in 1999, Human Resources at a tech startup in 2001, recognized for my work as a Case Manager at Casey Family Programs with disadvantaged youth in the foster care system in Oakland and the wider San Francisco-Bay Area in the early 2000's.",
    "Currently, I live in Sebastopol with my daughter, husband, and a menagerie of pets, where I enjoy gardening as it keeps me grounded and enjoying the beautiful weather.",
  ],
  registrationTitle: "Ready to Secure Your Family's Future?",
  registrationSub: "Free to attend. Limited seating available.",
  registrationCta: "Save My Seat",
  registrationFoot:
    "No pressure. Just clear information so you can make the right decision for your family.",
  errorTitle: "Something went wrong",
  errorBody:
    "We couldn't reserve your seat. Please try again or call our office.",
  validation: {
    firstName: "First name is required",
    lastName: "Last name is required",
    email: "Enter a valid email",
    phone: "Enter a valid phone number",
  },
  timeAgo: {
    monthAgo: "a month ago",
    monthsAgo: (n: number) => `${n} months ago`,
    yearAgo: "a year ago",
  },
};

export const lorsContentJune2 = { en: baseEn, es: baseEn } as unknown as Record<Lang, LorsCopy>;

export const NCBE_DIRECTIONS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=North+Coast+Builders+Exchange,+1030+Apollo+Way,+Santa+Rosa,+CA+95407";

export const getJune2Copy = (hParam: string | null): LorsCopy => {
  let idx = 0;
  if (hParam) {
    // Accept either a slug (preferred) or a 1-based numeric index.
    const slugIdx = june2Headlines.findIndex((h) => h.slug === hParam);
    if (slugIdx >= 0) {
      idx = slugIdx;
    } else {
      const n = Number(hParam);
      if (Number.isInteger(n) && n >= 1 && n <= june2Headlines.length) {
        idx = n - 1;
      }
    }
  }
  const pick = june2Headlines[idx];
  return { ...baseEn, headline: pick.headline, subhead: pick.subhead } as unknown as LorsCopy;
};
